import React, { useCallback, useEffect, useState } from "react";
import { Inbox, ClipboardList, LockKeyhole, LogOut, RefreshCw, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Eyebrow } from "../components/Shared";
import { getAdminKey, setAdminKey, verifyAdmin, listInquiries, listReviewRequests, updateInquiryStatus, updateReviewStatus, errorMessage } from "../lib/api";

const STATUSES = ["new", "contacted", "closed"];
const statusClass = { new: "bg-gold-200 text-ink-900", contacted: "bg-brand-100 text-brand-800", closed: "bg-sand-200 text-ink-700" };
const fmt = (iso) => new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

const Login = ({ onSuccess }) => {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyAdmin(key);
      setAdminKey(key);
      onSuccess();
    } catch (err) {
      toast.error("Invalid admin key");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="section">
      <div className="container-x">
        <form onSubmit={submit} className="mx-auto max-w-md rounded-3xl border border-brand-100 bg-white p-8" data-testid="admin-login">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-700"><LockKeyhole size={22} /></span>
          <h1 className="mt-5 font-serif text-3xl text-ink-900">Inquiries dashboard</h1>
          <p className="mt-2 text-sm text-ink-600">Enter the admin key to view contact leads and review requests.</p>
          <div className="mt-6 space-y-2">
            <Label htmlFor="admin-key">Admin key</Label>
            <Input id="admin-key" type="password" value={key} onChange={(e) => setKey(e.target.value)} className="h-11 rounded-xl" data-testid="admin-key-input" />
          </div>
          <Button type="submit" disabled={busy || !key} className="btn-pill mt-6 h-11 w-full bg-brand-600 text-white hover:bg-brand-700" data-testid="admin-login-btn">
            {busy ? "Checking..." : "Sign in"}
          </Button>
        </form>
      </div>
    </section>
  );
};

const Record = ({ r, kind, onStatus }) => (
  <div className="rounded-2xl border border-brand-100 bg-white p-5" data-testid={`${kind}-record`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-serif text-xl text-ink-900">{r.organization}</p>
        <p className="text-sm text-ink-600">{kind === "inquiry" ? r.name : `${r.contactName}${r.title ? `, ${r.title}` : ""}`}</p>
      </div>
      <div className="flex items-center gap-3">
        <Badge className={`rounded-full border-0 ${statusClass[r.status]}`}>{r.status}</Badge>
        <Select value={r.status} onValueChange={(v) => onStatus(r.id, v)}>
          <SelectTrigger className="h-9 w-[140px] rounded-full text-xs" data-testid={`${kind}-status-select`}><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink-700">
      <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 hover:text-brand-700"><Mail size={13} /> {r.email}</a>
      {r.phone && <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1.5 hover:text-brand-700"><Phone size={13} /> {r.phone}</a>}
      <span className="text-ink-500">{fmt(r.createdAt)}</span>
      {r.emails && (
        <span className={`inline-flex items-center gap-1 text-xs ${r.emails.team?.every((t) => t.ok) && r.emails.confirmation?.ok ? "text-brand-700" : "text-red-600"}`} data-testid={`${kind}-email-status`}>
          <Mail size={12} /> {r.emails.team?.every((t) => t.ok) && r.emails.confirmation?.ok ? "Emails sent" : "Email issue"}
        </span>
      )}
    </div>
    {kind === "inquiry" ? (
      <>
        {(r.orgType || r.interests?.length > 0) && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {r.orgType && <span className="rounded-full bg-sand-100 px-2.5 py-1 text-xs text-ink-700">{r.orgType}</span>}
            {r.interests?.map((i) => <span key={i} className="rounded-full bg-brand-50 px-2.5 py-1 text-xs text-brand-800">{i}</span>)}
          </div>
        )}
        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink-700">{r.message}</p>
      </>
    ) : (
      <>
        <div className="mt-3 flex flex-wrap gap-1.5 text-xs">
          <span className={`rounded-full px-2.5 py-1 ${r.isMember ? "bg-brand-50 text-brand-800" : "bg-sand-100 text-ink-700"}`}>{r.isMember ? "NHCN member" : "Not a member"}</span>
          {r.orgSize && <span className="rounded-full bg-sand-100 px-2.5 py-1 text-ink-700">{r.orgSize}</span>}
          {r.locations && <span className="rounded-full bg-sand-100 px-2.5 py-1 text-ink-700">{r.locations} location(s)</span>}
          {r.preferredTiming && <span className="rounded-full bg-sand-100 px-2.5 py-1 text-ink-700">Timing: {r.preferredTiming}</span>}
        </div>
        {r.notes && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink-700">{r.notes}</p>}
      </>
    )}
  </div>
);

const Admin = () => {
  const [authed, setAuthed] = useState(!!getAdminKey());
  const [inquiries, setInquiries] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([listInquiries(), listReviewRequests()]);
      setInquiries(a);
      setReviews(b);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAdminKey("");
        setAuthed(false);
      } else toast.error("Could not load records", { description: errorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const setStatus = (kind) => async (id, status) => {
    try {
      const fn = kind === "inquiry" ? updateInquiryStatus : updateReviewStatus;
      const updated = await fn(id, status);
      (kind === "inquiry" ? setInquiries : setReviews)((list) => list.map((x) => (x.id === id ? updated : x)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error("Update failed", { description: errorMessage(err) });
    }
  };

  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  const newCount = (l) => l.filter((x) => x.status === "new").length;

  return (
    <section className="section" data-testid="admin-dashboard">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Eyebrow>Internal</Eyebrow>
            <h1 className="mt-3 font-serif text-4xl text-ink-900">Inquiries dashboard</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={loading} className="btn-pill border-brand-200 text-brand-700 hover:bg-brand-50" data-testid="admin-refresh"><RefreshCw size={15} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
            <Button variant="ghost" onClick={() => { setAdminKey(""); setAuthed(false); }} className="btn-pill text-ink-700 hover:bg-sand-100" data-testid="admin-logout"><LogOut size={15} className="mr-1.5" /> Sign out</Button>
          </div>
        </div>
        <Tabs defaultValue="inquiries" className="mt-10">
          <TabsList className="h-auto rounded-full bg-white p-1 border border-brand-100">
            <TabsTrigger value="inquiries" className="rounded-full px-5 py-2 data-[state=active]:bg-brand-600 data-[state=active]:text-white" data-testid="tab-inquiries"><Inbox size={15} className="mr-2" /> Contact leads ({newCount(inquiries)} new)</TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-full px-5 py-2 data-[state=active]:bg-brand-600 data-[state=active]:text-white" data-testid="tab-reviews"><ClipboardList size={15} className="mr-2" /> Review requests ({newCount(reviews)} new)</TabsTrigger>
          </TabsList>
          <TabsContent value="inquiries" className="mt-6 space-y-4">
            {inquiries.length === 0 && !loading && <p className="text-sm text-ink-600" data-testid="inquiries-empty">No contact leads yet.</p>}
            {inquiries.map((r) => <Record key={r.id} r={r} kind="inquiry" onStatus={setStatus("inquiry")} />)}
          </TabsContent>
          <TabsContent value="reviews" className="mt-6 space-y-4">
            {reviews.length === 0 && !loading && <p className="text-sm text-ink-600" data-testid="reviews-empty">No review requests yet.</p>}
            {reviews.map((r) => <Record key={r.id} r={r} kind="review" onStatus={setStatus("review")} />)}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default Admin;
